/**
 * Transaction History Dialog component
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Timestamp } from "firebase/firestore";
import { AttachmentHistoryEntry, CustomFieldAttachmentValue, TransactionCustomField } from "../../../types";
import { TransactionHistoryEntry } from "../hooks/useTransactionHistory";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { api } from "../../../services/api";
import { getTransactionFriendlyId, truncateIdForDisplay } from "../../../utils/transactionUtils";

export interface TransactionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  transactionHistory: TransactionHistoryEntry[];
  loadingHistory: boolean;
  selectedTransaction: TransactionWithDate | null;
  workspaceId: string;
  /** Prefix for friendly transaction ID (e.g. "DV" for "DV-1"); when set, show ID next to title */
  transactionIdPrefix?: string;
  getUserNameForHistory: (userId: string) => string;
  getUserName: (userId: string) => string;
  getAttachmentIcon: (attachmentType: string | undefined, attachmentName: string | undefined) => ReactElement;
  /** Format date for display (respects workspace date format MM/DD or DD/MM) */
  formatDate: (date: Date | string | null | undefined) => string;
  /** Format time for display (respects workspace time format 12h/24h) */
  formatTime: (date: Date | string | null | undefined) => string;
  transactionCustomFields?: TransactionCustomField[];
  onAttachmentError?: (message: string) => void;
}

/**
 * Transaction History Dialog component
 */
export default function TransactionHistoryDialog({
  open,
  onClose,
  transactionHistory,
  loadingHistory,
  selectedTransaction,
  workspaceId,
  getUserNameForHistory,
  getUserName,
  getAttachmentIcon,
  formatDate,
  formatTime,
  transactionIdPrefix = "",
  transactionCustomFields = [],
  onAttachmentError,
}: TransactionHistoryDialogProps): ReactElement {
  const titleId = selectedTransaction
    ? (getTransactionFriendlyId(transactionIdPrefix, selectedTransaction.friendlyIdSequence) ?? selectedTransaction.id)
    : null;
  /** Open attachment in new tab. Open window synchronously on click so popup blockers allow it (desktop and mobile). */
  const openAttachment = useCallback(
    async (url: string) => {
      const newWindow = window.open("", "_blank");
      try {
        const { url: signedUrl } = await api.getAttachmentSignedUrl(workspaceId, url);
        if (newWindow) newWindow.location.href = signedUrl;
      } catch (e) {
        if (newWindow) newWindow.close();
        onAttachmentError?.(e instanceof Error ? e.message : "Could not open attachment");
      }
    },
    [workspaceId, onAttachmentError]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ minWidth: 0 }}>
            <Typography variant="h6">Transaction History</Typography>
            {titleId && (
              <Typography variant="body2" color="text.secondary" title={titleId} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>ID: {truncateIdForDisplay(titleId)}</Typography>
            )}
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loadingHistory ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {transactionHistory.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No change history available for this transaction.
                </Typography>
              </Box>
            ) : (
              transactionHistory.map((entry) => (
                <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" fontWeight="medium">
                        {getUserNameForHistory(entry.userId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.timestamp.toDate())} {formatTime(entry.timestamp.toDate())}
                      </Typography>
                    </Box>
                    <Divider />
                    <Stack spacing={0.5}>
                      {entry.changes.map((change, index) => {
                        // Check if this change is about an attachment and has a URL
                        const isAttachmentChange = entry.attachmentUrl && (
                          change.includes("Attachment added:") ||
                          change.includes("Attachment replaced:")
                        );
                        
                        if (isAttachmentChange && entry.attachmentUrl) {
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.primary" sx={{ flex: 1 }}>
                                {change}
                              </Typography>
                              <Tooltip title="Download attachment">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(entry.attachmentUrl!, "_blank");
                                  }}
                                  color="primary"
                                  sx={{ ml: 1 }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        }
                        
                        return (
                          <Typography key={index} variant="body2" color="text.primary">
                            {change}
                          </Typography>
                        );
                      })}
                    </Stack>
                    {entry.customFieldValues && Object.keys(entry.customFieldValues).length > 0 && transactionCustomFields.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
                        <Stack spacing={0.75}>
                          {transactionCustomFields
                            .filter((f) => entry.customFieldValues?.[f.id] != null)
                            .map((field) => {
                            const value = entry.customFieldValues![field.id];
                            const label = field.label;
                            const isAttachment = value && typeof value === "object" && "url" in value;
                            const attachmentVal = isAttachment ? (value as CustomFieldAttachmentValue) : null;
                            if (!attachmentVal?.url) return null;
                            return (
                              <Box
                                key={field.id}
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                              >
                                {getAttachmentIcon(attachmentVal.type, attachmentVal.name)}
                                <Typography variant="body2" sx={{ flex: 1 }}>{label}: {attachmentVal.name}</Typography>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAttachment(attachmentVal.url);
                                    }}
                                    color="primary"
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ))
            )}
            
            {/* Attachment History Section */}
            {selectedTransaction && (() => {
              // Get attachment history (or initialize with current attachment for backward compatibility)
              let attachmentHistory: AttachmentHistoryEntry[] = selectedTransaction.attachmentHistory || [];
              if (selectedTransaction.attachmentUrl && attachmentHistory.length === 0) {
                attachmentHistory = [{
                  url: selectedTransaction.attachmentUrl,
                  name: selectedTransaction.attachmentName || "",
                  type: selectedTransaction.attachmentType || "",
                  uploadedAt: selectedTransaction.createdAt as Timestamp,
                  uploadedBy: selectedTransaction.userId,
                }];
              }
              
              if (attachmentHistory.length > 0) {
                // Sort by upload date (newest first)
                const sortedHistory = [...attachmentHistory].sort((a, b) => {
                  let dateA = 0;
                  let dateB = 0;
                  try {
                    if (a.uploadedAt instanceof Timestamp) {
                      dateA = (a.uploadedAt as Timestamp).toDate().getTime();
                    } else if (a.uploadedAt && typeof a.uploadedAt === "object" && "seconds" in a.uploadedAt) {
                      const seconds = (a.uploadedAt as any).seconds;
                      if (seconds && typeof seconds === "number") {
                        dateA = seconds * 1000;
                      }
                    }
                  } catch (error) {
                    console.error("Error parsing dateA:", error);
                  }
                  try {
                    if (b.uploadedAt instanceof Timestamp) {
                      dateB = (b.uploadedAt as Timestamp).toDate().getTime();
                    } else if (b.uploadedAt && typeof b.uploadedAt === "object" && "seconds" in b.uploadedAt) {
                      const seconds = (b.uploadedAt as any).seconds;
                      if (seconds && typeof seconds === "number") {
                        dateB = seconds * 1000;
                      }
                    }
                  } catch (error) {
                    console.error("Error parsing dateB:", error);
                  }
                  return dateB - dateA; // Newest first
                });
                
                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AttachFileIcon fontSize="small" />
                      Attachment History ({sortedHistory.length})
                    </Typography>
                    <Stack spacing={1.5}>
                      {sortedHistory.map((entry, index) => {
                        const isCurrent = entry.url === selectedTransaction.attachmentUrl;
                        let uploadDate: Date;
                        try {
                          if (entry.uploadedAt instanceof Timestamp) {
                            uploadDate = (entry.uploadedAt as Timestamp).toDate();
                          } else if (entry.uploadedAt && typeof entry.uploadedAt === "object" && "seconds" in entry.uploadedAt) {
                            const seconds = (entry.uploadedAt as any).seconds;
                            if (seconds && typeof seconds === "number") {
                              uploadDate = new Date(seconds * 1000);
                            } else {
                              uploadDate = new Date();
                            }
                          } else {
                            uploadDate = new Date();
                          }
                          // Validate date
                          if (isNaN(uploadDate.getTime())) {
                            uploadDate = new Date();
                          }
                        } catch (error) {
                          console.error("Error parsing upload date:", error, entry.uploadedAt);
                          uploadDate = new Date();
                        }
                        
                        return (
                          <Paper key={`${entry.url}-${index}`} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                                  {getAttachmentIcon(entry.type, entry.name)}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                      {entry.name}
                                      {isCurrent && (
                                        <Chip label="Current" size="small" sx={{ ml: 1, height: 20 }} />
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Tooltip title="Download attachment">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAttachment(entry.url);
                                    }}
                                    color="primary"
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Divider />
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="caption" color="text.secondary">
                                  {getUserName(entry.uploadedBy)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(uploadDate)} {formatTime(uploadDate)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              }
              return null;
            })()}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
